import { DatePipe } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { DeployMessage, DeploySendAttempt } from '@core/models/deploy-plan-config.model';
import { ICONS } from '@core/services/icons/icons.service';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import {
  StatusPillComponent,
  StatusPillVariant
} from '@shared/components/status-pill/status-pill.component';

import { DeployRecipientsCellComponent } from '../deploy-recipients-cell/deploy-recipients-cell.component';
import {
  hasSuccessfulSend,
  isResendLocked,
  isSendLocked,
  lastAttemptFailed
} from '../deploy-selection.utils';
import { DeployWarningsListComponent } from '../deploy-warnings-list/deploy-warnings-list.component';

interface DeployMessageRow {
  message: DeployMessage;
  selected: boolean;
  locked: boolean;
  /** Locked specifically because it already went out and re-sending is off. */
  alreadySent: boolean;
  /**
   * A successful attempt exists. Deliberately independent of `allowRedeploy`, unlike
   * `alreadySent`: whether a message went out is a fact about the message, where being
   * locked is a fact about the configuration. Tying the two hid the "Sent" badge on exactly
   * the channels that re-send, which are the ones where it matters most.
   */
  hasSuccessfulSend: boolean;
  /** In an accepted deploy that has not reported back yet. */
  sending: boolean;
  /** The most recent attempt, which is the one whose outcome the row reports. */
  lastSend: DeploySendAttempt | null;
  /** The most recent attempt failed. Held apart from `lastFailure`, which may be absent. */
  lastFailed: boolean;
  /** Why the most recent attempt failed; null when it succeeded or gave no reason. */
  lastFailure: string | null;
  /** Whether the row has anything to expand: its warnings, its failure, or both. */
  hasDetail: boolean;
  /** What the channel created, from the most recent attempt that created anything. */
  externalId: string | null;
  warningCount: number;
  /** Null when the row has nothing to add: a sent, valid, warning-free message. */
  status: { variant: StatusPillVariant; key: string } | null;
}

/**
 * The id of whatever the channel last created, which is not the latest attempt's: a failed
 * GedMouv redeploy carries none while the tour its earlier attempt posted stays live at
 * GedMouv. That id is the one thing the operator needs, since `postTour` is not idempotent
 * and has no cancel — the duplicate has to be deleted by hand before resending.
 */
const lastExternalId = (sends: readonly DeploySendAttempt[]): string | null => {
  for (let index = sends.length - 1; index >= 0; index--) {
    const { externalId } = sends[index];
    if (externalId !== null) return externalId;
  }
  return null;
};

/**
 * A sent message shows no status pill — its "Sent" badge already says the useful thing, and an
 * "OK" beside it read as though it were still pending. A message whose latest attempt failed is
 * silent here for the same reason: its own "Failed" badge says it, and the "OK" this used to
 * emit claimed the opposite of what the row's red date reported.
 *
 * The surviving "OK" is **grey, not green**. It means "valid, nothing to flag, not yet sent" —
 * a readiness, not an outcome — and green is what this table uses for a message that actually
 * went out. In green the two were indistinguishable, and a listing of a hundred unsent rows
 * read as though all of them were already out.
 */
const statusOf = (
  message: DeployMessage,
  hasSuccessfulSend: boolean,
  lastFailed: boolean
) => {
  if (!message.isValid) {
    return {
      variant: 'danger' as StatusPillVariant,
      key: 'features.plan.deploy.table.invalid'
    };
  }
  if (message.warnings.length > 0) {
    return {
      variant: 'warning' as StatusPillVariant,
      key: 'features.plan.deploy.table.warning_count'
    };
  }
  if (hasSuccessfulSend || lastFailed) return null;
  return { variant: 'neutral' as StatusPillVariant, key: 'features.plan.deploy.status.ok' };
};

@Component({
  selector: 'app-deploy-messages-table',
  imports: [
    TranslatePipe,
    DatePipe,
    CheckboxComponent,
    StatusPillComponent,
    DeployWarningsListComponent,
    DeployRecipientsCellComponent
  ],
  templateUrl: './deploy-messages-table.component.html',
  styles: ``
})
export class DeployMessagesTableComponent {
  readonly messages = input.required<DeployMessage[]>();
  readonly selectedUids = input.required<ReadonlySet<string>>();
  readonly allowRedeploy = input.required<boolean>();
  readonly supportsDeployInSteps = input.required<boolean>();
  /** Owned by the panel; narrows the listing to what is currently ticked. */
  readonly selectedOnly = input.required<boolean>();
  /** Uids in an accepted deploy that has not reported back yet. */
  readonly sendingUids = input<ReadonlySet<string>>(new Set<string>());

  readonly toggleUid = output<string>();
  /** The whole selection, replaced — what the header box emits. */
  readonly selectionRequested = output<string[]>();
  readonly viewMessage = output<DeployMessage>();
  readonly viewHistory = output<DeployMessage>();

  protected readonly ICONS = ICONS;

  protected readonly rows = computed<DeployMessageRow[]>(() => {
    const selected = this.selectedUids();
    const allowRedeploy = this.allowRedeploy();
    const onlySelected = this.selectedOnly();
    const sending = this.sendingUids();

    return this.messages()
      .filter((message) => !onlySelected || selected.has(message.uid))
      .map((message) => {
        const succeeded = hasSuccessfulSend(message);
        const isSending = sending.has(message.uid);
        const lastSend =
          message.sends.length > 0 ? message.sends[message.sends.length - 1] : null;
        const lastFailed = lastAttemptFailed(message);
        const lastFailure = lastFailed ? (lastSend?.error ?? null) : null;
        return {
          message,
          selected: selected.has(message.uid),
          // A row already on its way cannot be untangled from the request that carries it.
          locked: isSending || isSendLocked(message, allowRedeploy),
          alreadySent: isResendLocked(message, allowRedeploy),
          hasSuccessfulSend: succeeded,
          sending: isSending,
          lastSend,
          lastFailed,
          lastFailure,
          hasDetail: message.warnings.length > 0 || lastFailure !== null,
          externalId: lastExternalId(message.sends),
          warningCount: message.warnings.length,
          status: statusOf(message, succeeded, lastFailed)
        };
      });
  });

  /**
   * What the header box acts on: the rows listed right now, minus the ones that cannot be
   * sent. Scoping it to the listing is what keeps "all" meaning what the operator can see
   * — with the selected-only filter on, the listing *is* the selection.
   */
  private readonly selectableUids = computed(() =>
    this.rows()
      .filter((row) => !row.locked)
      .map((row) => row.message.uid)
  );

  /** What a detail row spans: every column, the checkbox one included when it is shown. */
  protected readonly columnCount = computed(() => (this.supportsDeployInSteps() ? 7 : 6));

  protected readonly noneSelectable = computed(() => this.selectableUids().length === 0);

  protected readonly allSelected = computed(() => {
    const uids = this.selectableUids();
    const selected = this.selectedUids();
    return uids.length > 0 && uids.every((uid) => selected.has(uid));
  });

  protected readonly someSelected = computed(() => {
    const selected = this.selectedUids();
    return !this.allSelected() && this.selectableUids().some((uid) => selected.has(uid));
  });

  protected onToggleAll(): void {
    this.selectionRequested.emit(this.allSelected() ? [] : this.selectableUids());
  }

  protected onToggle(row: DeployMessageRow): void {
    if (row.locked) return;
    this.toggleUid.emit(row.message.uid);
  }

  /**
   * Collapsed by default: a configuration can hold hundreds of messages, and a repair list
   * open on every failing one buries the rows that still need reading. A channel's failure
   * text can also run to hundreds of characters over several lines, which inline would leave
   * the useful columns unreadable.
   */
  private readonly expandedUids = signal<ReadonlySet<string>>(new Set());

  protected isExpanded(uid: string): boolean {
    return this.expandedUids().has(uid);
  }

  protected toggleDetail(uid: string): void {
    this.expandedUids.update((expanded) => {
      const next = new Set(expanded);
      if (!next.delete(uid)) next.add(uid);
      return next;
    });
  }
}
